/**
 * ScreenLayout Component
 * Reusable wrapper for screens with gradient background, status bar, and back button
 */

import React, { useMemo } from 'react';
import { View } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useTheme } from '../../src/theme/useTheme';
import { useThemeContext } from '../../src/theme/ThemeContext';
import { HeaderBackButton } from './HeaderBackButton/HeaderBackButton';
import { getScreenLayoutStyles } from './ScreenLayout.styles';

interface ScreenLayoutProps {
  children: React.ReactNode;
  showBackButton?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  onBack?: () => void;
  /** Test ID for testing */
  testID?: string;
}

export const ScreenLayout: React.FC<ScreenLayoutProps> = ({
  children,
  showBackButton = true,
  containerStyle,
  onBack,
  testID,
}) => {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const theme = useTheme();
  const { isDark } = useThemeContext();
  const styles = useMemo(() => getScreenLayoutStyles(theme, insets), [theme, insets]);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      router.back();
    }
  };

  return (
    <View
      style={[styles.container, containerStyle]}
      testID={testID}
    >
      <StatusBar style={isDark ? 'light' : 'dark'} />
      {showBackButton && (
        <View style={styles.header} testID={testID ? `${testID}-header` : 'screen-layout-header'}>
          <HeaderBackButton onPress={handleBack} testID={testID ? `${testID}-back-button` : 'screen-layout-back-button'} />
        </View>
      )}
      {children}
    </View>
  );
};

